import { useState } from "react";
import { ExternalLink, ListChecks, RotateCcw } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  SegmentBar,
  cn
} from "@tutti-os/ui-system";
import type {
  TuttiPlanIssueSnapshot,
  TuttiPlanIssueTaskSnapshot
} from "../workspaceWorkflowRuntime";
import type { TuttiModePlanTaskPromptAction } from "./tuttiModePlanPromptActions";

export interface TuttiPlanIssuePanelLabels {
  openIssue: string;
  listView: string;
  boardView: string;
  parallelizable: string;
  autoAccept: string;
  accept: string;
  rework: string;
  dependencies: string;
  stageParallel: (index: string, count: string) => string;
  stageSequential: (index: string) => string;
  summary: (done: string, total: string, running: string) => string;
  statusNotStarted: string;
  statusRunning: string;
  statusPendingAcceptance: string;
  statusCompleted: string;
  statusFailed: string;
  statusCanceled: string;
}

type TuttiPlanIssueViewMode = "list" | "board";

const BOARD_STATUS_ORDER = [
  "not_started",
  "running",
  "pending_acceptance",
  "completed",
  "failed",
  "canceled"
] as const;

type BoardStatus = (typeof BOARD_STATUS_ORDER)[number];

function boardStatusOf(task: TuttiPlanIssueTaskSnapshot): BoardStatus {
  return (BOARD_STATUS_ORDER as readonly string[]).includes(task.status)
    ? (task.status as BoardStatus)
    : "not_started";
}

function statusLabel(
  labels: TuttiPlanIssuePanelLabels,
  status: BoardStatus
): string {
  switch (status) {
    case "running":
      return labels.statusRunning;
    case "pending_acceptance":
      return labels.statusPendingAcceptance;
    case "completed":
      return labels.statusCompleted;
    case "failed":
      return labels.statusFailed;
    case "canceled":
      return labels.statusCanceled;
    default:
      return labels.statusNotStarted;
  }
}

function statusDotClassName(status: BoardStatus): string {
  switch (status) {
    case "running":
      return "bg-[var(--status-running)]";
    case "pending_acceptance":
      return "bg-[var(--tutti-purple)]";
    case "completed":
      return "bg-[var(--state-success)]";
    case "failed":
      return "bg-[var(--state-danger)]";
    case "canceled":
      return "bg-[var(--text-tertiary)]";
    default:
      return "bg-[var(--text-secondary)]";
  }
}

type TuttiPlanIssueStage = {
  kind: "parallel" | "sequential";
  tasks: TuttiPlanIssueTaskSnapshot[];
};

/** Mirrors the dispatcher grouping: consecutive parallelizable tasks share a
 * stage only while they stay independent of each other — a task that depends
 * on a member of the running stage can never actually run alongside it
 * (dependencies outrank the flag at dispatch), so it starts a new stage. */
export function groupTuttiPlanIssueTasksIntoStages(
  tasks: readonly TuttiPlanIssueTaskSnapshot[]
): TuttiPlanIssueStage[] {
  const ordered = [...tasks].sort(
    (left, right) => left.sortIndex - right.sortIndex
  );
  const stages: TuttiPlanIssueStage[] = [];
  for (const task of ordered) {
    const previous = stages.at(-1);
    if (
      task.parallelizable &&
      previous?.kind === "parallel" &&
      !task.dependencyTaskIds.some((dependencyId) =>
        previous.tasks.some((member) => member.taskId === dependencyId)
      )
    ) {
      previous.tasks.push(task);
      continue;
    }
    stages.push({
      kind: task.parallelizable ? "parallel" : "sequential",
      tasks: [task]
    });
  }
  return stages;
}

/** A task action that the source conversation turns into an Agent draft. */
export type TuttiPlanIssueTaskAction = TuttiModePlanTaskPromptAction;

/** A task that has launched has a delegate conversation to jump into. */
function taskHasConversation(task: TuttiPlanIssueTaskSnapshot): boolean {
  return task.status !== "not_started";
}

/**
 * Embedded "issue panel view" for the source conversation: once the accepted
 * plan materialized an Issue, the conversation shows its subtasks as a live
 * board/list. Pending and failed tasks can prepare accept/rework instructions
 * in the source Agent composer; the panel itself does not mutate the managed
 * Issue. A jump into the full Issue surface remains one click away.
 */
export function TuttiPlanIssuePanel({
  embedded = false,
  issue,
  labels,
  onOpenIssue,
  onTaskAction,
  onOpenTask
}: {
  embedded?: boolean;
  issue: TuttiPlanIssueSnapshot;
  labels: TuttiPlanIssuePanelLabels;
  onOpenIssue?: () => void;
  onTaskAction?: (
    taskId: string,
    action: TuttiPlanIssueTaskAction
  ) => Promise<void>;
  /** Jump into the delegate conversation of a task that has launched. */
  onOpenTask?: (taskId: string) => void | Promise<void>;
}): React.JSX.Element {
  const [viewMode, setViewMode] = useState<TuttiPlanIssueViewMode>("board");
  const [decidingTaskIds, setDecidingTaskIds] = useState<readonly string[]>([]);
  const requestTaskAction = onTaskAction
    ? (taskId: string, action: TuttiPlanIssueTaskAction): void => {
        setDecidingTaskIds((current) =>
          current.includes(taskId) ? current : [...current, taskId]
        );
        void onTaskAction(taskId, action)
          .catch((error: unknown) => {
            // Keep prompt preparation failures observable; otherwise a rejected
            // host callback would read as a dead button.
            console.error("tutti plan issue task prompt action failed", error);
          })
          .finally(() => {
            setDecidingTaskIds((current) =>
              current.filter((id) => id !== taskId)
            );
          });
      }
    : undefined;
  const done = issue.tasks.filter((task) => task.status === "completed").length;
  const running = issue.tasks.filter(
    (task) => task.status === "running"
  ).length;
  return (
    <Card
      size="sm"
      className={cn(
        "w-full pt-2!",
        embedded && "border-0 bg-transparent shadow-none"
      )}
      data-testid="tutti-plan-issue-panel"
    >
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <ListChecks
              aria-hidden
              className="size-4 shrink-0 text-[var(--tutti-purple)]"
            />
            <span className="min-w-0 truncate text-sm font-medium text-foreground">
              {issue.title}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {labels.summary(
                String(done),
                String(issue.tasks.length),
                String(running)
              )}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <SegmentBar
              segments={[
                {
                  label: labels.listView,
                  testId: "tutti-plan-issue-view-list",
                  value: "list"
                },
                {
                  label: labels.boardView,
                  testId: "tutti-plan-issue-view-board",
                  value: "board"
                }
              ]}
              value={viewMode}
              onValueChange={setViewMode}
            />
            {onOpenIssue ? (
              <Button
                type="button"
                variant="secondary"
                data-testid="tutti-plan-issue-open"
                onClick={onOpenIssue}
              >
                <ExternalLink aria-hidden className="size-3.5" />
                {labels.openIssue}
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {viewMode === "board" ? (
          <TuttiPlanIssueBoard
            issue={issue}
            labels={labels}
            requestTaskAction={requestTaskAction}
            decidingTaskIds={decidingTaskIds}
            openTask={onOpenTask}
          />
        ) : (
          <TuttiPlanIssueList
            issue={issue}
            labels={labels}
            requestTaskAction={requestTaskAction}
            decidingTaskIds={decidingTaskIds}
            openTask={onOpenTask}
          />
        )}
      </CardContent>
    </Card>
  );
}

function TaskStructureChips({
  labels,
  task
}: {
  labels: TuttiPlanIssuePanelLabels;
  task: TuttiPlanIssueTaskSnapshot;
}): React.JSX.Element | null {
  const dependencies = task.dependencyTaskIds.filter((id) => id.trim() !== "");
  if (!task.parallelizable && !task.autoAccept && dependencies.length === 0) {
    return null;
  }
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      {task.parallelizable ? (
        <Badge variant="accent" size="sm">
          {labels.parallelizable}
        </Badge>
      ) : null}
      {task.autoAccept ? (
        <Badge variant="success" size="sm">
          {labels.autoAccept}
        </Badge>
      ) : null}
      {dependencies.length > 0 ? (
        <Badge
          variant="secondary"
          size="sm"
          className="min-w-0 truncate"
          title={dependencies.join(", ")}
        >
          {labels.dependencies}: {dependencies.join(", ")}
        </Badge>
      ) : null}
    </span>
  );
}

function TaskPromptActions({
  labels,
  task,
  requestTaskAction,
  deciding
}: {
  labels: TuttiPlanIssuePanelLabels;
  task: TuttiPlanIssueTaskSnapshot;
  requestTaskAction?: (
    taskId: string,
    action: TuttiPlanIssueTaskAction
  ) => void;
  deciding: boolean;
}): React.JSX.Element | null {
  // These buttons only prepare source-Agent instructions. The Agent remains
  // responsible for inspecting the authoritative execution before acting.
  const canAccept = task.status === "pending_acceptance";
  const canRework = canAccept || task.status === "failed";
  if (!requestTaskAction || !canRework) {
    return null;
  }
  return (
    <span className="flex shrink-0 items-center gap-1">
      {canAccept ? (
        <Button
          type="button"
          size="sm"
          className="h-6 bg-[var(--tutti-purple)] px-2 text-[11px] text-white hover:bg-[color-mix(in_srgb,var(--tutti-purple)_85%,black)]"
          disabled={deciding}
          data-testid={`tutti-plan-issue-accept-${task.taskId}`}
          onClick={(event) => {
            // The card itself jumps into the delegate conversation; preparing
            // a source-Agent instruction must not trigger that navigation.
            event.stopPropagation();
            requestTaskAction(task.taskId, "accept");
          }}
        >
          {labels.accept}
        </Button>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-6 px-2 text-[11px]"
        disabled={deciding}
        data-testid={`tutti-plan-issue-rework-${task.taskId}`}
        onClick={(event) => {
          event.stopPropagation();
          requestTaskAction(task.taskId, "rework");
        }}
      >
        <RotateCcw aria-hidden className="size-3.5" />
        {labels.rework}
      </Button>
    </span>
  );
}

function TuttiPlanIssueBoard({
  issue,
  labels,
  requestTaskAction,
  decidingTaskIds,
  openTask
}: {
  issue: TuttiPlanIssueSnapshot;
  labels: TuttiPlanIssuePanelLabels;
  requestTaskAction?: (
    taskId: string,
    action: TuttiPlanIssueTaskAction
  ) => void;
  decidingTaskIds: readonly string[];
  openTask?: (taskId: string) => void | Promise<void>;
}): React.JSX.Element {
  const groups = new Map<BoardStatus, TuttiPlanIssueTaskSnapshot[]>();
  for (const task of issue.tasks) {
    const status = boardStatusOf(task);
    groups.set(status, [...(groups.get(status) ?? []), task]);
  }
  const columns = BOARD_STATUS_ORDER.filter((status) =>
    status === "failed" || status === "canceled"
      ? (groups.get(status)?.length ?? 0) > 0
      : true
  );
  return (
    <div className="board-scroll min-w-0 overflow-x-auto pb-1">
      <div
        className="grid gap-2"
        style={{
          gridTemplateColumns: `repeat(${columns.length}, minmax(280px, 1fr))`
        }}
      >
        {columns.map((status) => {
          const tasks = groups.get(status) ?? [];
          return (
            <div
              key={status}
              className="min-h-[220px] min-w-0 rounded-lg border border-[var(--line-2)] bg-muted/30 px-2 py-2"
              data-testid={`tutti-plan-issue-column-${status}`}
            >
              <div className="mx-1 mb-1.5 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span
                    aria-hidden
                    className={cn(
                      "size-1.5 rounded-full",
                      statusDotClassName(status)
                    )}
                  />
                  <span className="truncate text-[11px] font-medium text-foreground">
                    {statusLabel(labels, status)}
                  </span>
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {tasks.length}
                </span>
              </div>
              <div className="grid gap-1.5">
                {tasks.map((task) => {
                  const openable = openTask && taskHasConversation(task);
                  return (
                    <div
                      key={task.taskId}
                      role={openable ? "button" : undefined}
                      tabIndex={openable ? 0 : undefined}
                      data-testid={`tutti-plan-issue-task-${task.taskId}`}
                      className={cn(
                        "min-w-0 overflow-hidden rounded-md bg-[var(--background-board-card)] p-3",
                        openable && "cursor-pointer"
                      )}
                      onClick={
                        openable ? () => void openTask(task.taskId) : undefined
                      }
                      onKeyDown={
                        openable
                          ? (event) => {
                              if (event.key === "Enter") {
                                void openTask(task.taskId);
                              }
                            }
                          : undefined
                      }
                    >
                      <span className="line-clamp-2 text-[13px] font-medium text-foreground">
                        {task.title}
                      </span>
                      <span className="mt-2 block empty:hidden">
                        <TaskStructureChips labels={labels} task={task} />
                      </span>
                      <span className="mt-1.5 block empty:hidden">
                        <TaskPromptActions
                          labels={labels}
                          task={task}
                          requestTaskAction={requestTaskAction}
                          deciding={decidingTaskIds.includes(task.taskId)}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TuttiPlanIssueList({
  issue,
  labels,
  requestTaskAction,
  decidingTaskIds,
  openTask
}: {
  issue: TuttiPlanIssueSnapshot;
  labels: TuttiPlanIssuePanelLabels;
  requestTaskAction?: (
    taskId: string,
    action: TuttiPlanIssueTaskAction
  ) => void;
  decidingTaskIds: readonly string[];
  openTask?: (taskId: string) => void | Promise<void>;
}): React.JSX.Element {
  const showStages = issue.tasks.some((task) => task.parallelizable);
  const stages = showStages
    ? groupTuttiPlanIssueTasksIntoStages(issue.tasks)
    : [{ kind: "sequential" as const, tasks: [...issue.tasks] }];
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--line-2)] bg-[var(--background-board-card)]">
      {stages.map((stage, index) => (
        <div key={`stage-${index}`}>
          {showStages ? (
            <div
              className="border-b border-[var(--line-2)] bg-muted/40 px-3 py-1 text-[10px] font-medium text-muted-foreground"
              data-testid={`tutti-plan-issue-stage-${stage.kind}`}
            >
              {stage.kind === "parallel"
                ? labels.stageParallel(
                    String(index + 1),
                    String(stage.tasks.length)
                  )
                : labels.stageSequential(String(index + 1))}
            </div>
          ) : null}
          {stage.tasks.map((task) => {
            const status = boardStatusOf(task);
            const openable = openTask && taskHasConversation(task);
            return (
              <div
                key={task.taskId}
                role={openable ? "button" : undefined}
                tabIndex={openable ? 0 : undefined}
                data-testid={`tutti-plan-issue-row-${task.taskId}`}
                className={cn(
                  "flex items-start justify-between gap-3 border-b border-[var(--line-2)] px-3 py-2 last:border-b-0",
                  openable && "cursor-pointer"
                )}
                onClick={
                  openable ? () => void openTask(task.taskId) : undefined
                }
                onKeyDown={
                  openable
                    ? (event) => {
                        if (event.key === "Enter") {
                          void openTask(task.taskId);
                        }
                      }
                    : undefined
                }
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className="truncate text-[13px] font-medium text-foreground">
                      {task.title}
                    </span>
                    <TaskStructureChips labels={labels} task={task} />
                  </div>
                  {task.content ? (
                    <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                      {task.content}
                    </p>
                  ) : null}
                </div>
                <span className="flex shrink-0 items-center gap-2">
                  <TaskPromptActions
                    labels={labels}
                    task={task}
                    requestTaskAction={requestTaskAction}
                    deciding={decidingTaskIds.includes(task.taskId)}
                  />
                  <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span
                      aria-hidden
                      className={cn(
                        "size-1.5 rounded-full",
                        statusDotClassName(status)
                      )}
                    />
                    {statusLabel(labels, status)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
